import { Component, OnInit, ViewEncapsulation } from '@angular/core'
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators'
import { Observable, of, interval } from 'rxjs'

import { navigationAdmin } from '@shared/mock-data/navigation-admin'
import { navigationClient } from '@shared/mock-data/navigation-client'
import { navigationReseller } from '@shared/mock-data/navigation-reseller'
import { SidebarService } from '../sidebar/sidebar.service'
import { AuthService } from '@app/modules/auth/auth.service'
import { Navigation } from '@shared/models/navigation.model'
import { PermissionsSource } from '@core/types/permissions.source'
import { PlatformSettingsService } from '@core/services/platfrom-settings.service'
import { User } from '@shared/models/user.model'
import { PlatformSettings } from '@shared/models/platform-settings.model'
import { DestroyService } from '@core/services/destroy.service'
import { IntegrationsService } from '@app/modules/client/config/integrations/integrations.service'
import { Integration, IntegrationModelStatus, IntegrationTypes } from '@shared/models/integration.model'
import { ClientStatus } from '@app/shared/models/client.model'
import moment from 'moment-timezone'
import { SettingsService } from '@app/shared/common-components/settings/settings.service'
import { SettingName } from '@app/shared/models/setting.model'

@Component({
  selector: 'lin-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class NavbarComponent implements OnInit {
  isOpen = false
  navigation: Navigation[] = []
  private originalMenu: Navigation[] = []
  private user: User

  constructor(
    private sidebarService: SidebarService,
    private authService: AuthService,
    private readonly platformSettingsService: PlatformSettingsService,
    private readonly integrationsService: IntegrationsService,
    private readonly destroyService: DestroyService,
    private readonly settingsService: SettingsService,
  ) {}

  ngOnInit(): void {
    const navigationMenu = {
      admin: navigationAdmin,
      client: navigationClient,
      reseller: navigationReseller,
    }

    this.authService.currentUser$
      .pipe(
        filter(Boolean),
        switchMap((user: User) => {
          this.user = user
          this.originalMenu = [...(navigationMenu?.[user?.teamType] || [])]

          return this.settingsService.getSettings(user.team.id).pipe(
            tap((settings) => {
              const blockout = !!settings.find((s) => s.name === SettingName.Blockout)?.value
              this.applyNavigationFilter(blockout)

              interval(60000)
                .pipe(takeUntil(this.destroyService))
                .subscribe(() => this.applyNavigationFilter(blockout))
            }),
            tap(() => this.addSupportToMenu()),
            switchMap(() => this.initIntegrations()),
            switchMap(() => this.initPolicyLinks()),
          )
        }),
        takeUntil(this.destroyService),
      )
      .subscribe(() => this.sidebarService.setNavbarItems(this.navigation))
  }

  private applyNavigationFilter(blockout: boolean): void {
    if (this.user.isClient()) {
      const isDriverOrManager = ['Driver', 'Manager'].includes(this.user.role.displayName)
      const isOutsideAllowedHours = this.isOutsideAllowedHours()

      if (blockout && isDriverOrManager && isOutsideAllowedHours) {
        // Restrict menu items when outside 9AM–5PM
        this.navigation = this.originalMenu.filter(
          (item) =>
            !['/client/map', '/client/reports', '/client/driving-behaviour', '/client/driving-behaviour-upd', '/client/messenger'].includes(
              item.url,
            ),
        )
        this.sidebarService.setNavbarItems(this.navigation)
        return
      }
    }

    this.navigation = [...this.originalMenu]
    this.sidebarService.setNavbarItems(this.navigation)
  }

  private isOutsideAllowedHours(): boolean {
    if (!this.user?.timezone?.name) return false

    const now = moment().tz(this.user.timezone.name)
    const hour = now.hour()
    const minute = now.minute()

    // Block before 9:00 AM or after 5:00 PM
    return hour < 9 || hour > 17 || (hour === 17 && minute > 0)
  }

  initPolicyLinks(): Observable<PlatformSettings> {
    return this.platformSettingsService.platformSettings$.pipe(
      filter(Boolean),
      tap((platformSettings) => {
        if (platformSettings.privacyPolicyLink) {
          const privacyItem = {
            title: 'NAV.PRIVACY_POLICY',
            icon: 'privacy_policy',
            url: platformSettings.privacyPolicyLink,
            externalLink: true,
          }
          this.originalMenu.push(privacyItem)
          this.navigation.push(privacyItem)
        }

        if (platformSettings.termsOfUseLink) {
          const termsItem = {
            title: 'NAV.TERMS_OF_USE',
            icon: 'terms_of_use',
            url: platformSettings.termsOfUseLink,
            externalLink: true,
          }
          this.originalMenu.push(termsItem)
          this.navigation.push(termsItem)
        }
      }),
    )
  }

  initIntegrations(): Observable<Integration[]> {
    const availableIntegrations = [IntegrationTypes.Solbox, IntegrationTypes.Logmaster]
    const integrationKey = (integrationName: string) => integrationName.replace(/[0-9. ]/g, '')

    return this.user.isClient() && this.user.team?.clientStatus !== ClientStatus.BlockedBilling
      ? this.integrationsService.integrationsList(this.user.team.id).pipe(
          map((integrations) =>
            integrations.filter((integration) =>
              availableIntegrations.includes(<IntegrationTypes>integrationKey(integration.name).toLowerCase()),
            ),
          ),
          tap((integrations) => {
            if (!integrations.length) return

            integrations.forEach((integration) => {
              if (integration.status === IntegrationModelStatus.Enabled) {
                const navItem = {
                  title: `NAV.${integrationKey(integration.name).toUpperCase()}`,
                  icon: `${integrationKey(integration.name).toLowerCase()}_icon`,
                  permission: PermissionsSource.SUPPORT,
                  url:
                    integrationKey(integration.name).toLowerCase() === IntegrationTypes.LinxioVision
                      ? 'https://ap-ftcloud.ifleetvision.com/login/'
                      : `/client/${integrationKey(integration.name).toLowerCase()}`,
                  ...(integrationKey(integration.name).toLowerCase() === IntegrationTypes.LinxioVision && { externalLink: true }),
                }
                this.originalMenu.push(navItem)
                this.navigation.push(navItem)
              }
            })
          }),
        )
      : of(null)
  }

  getSidebarFoldedIcon(): string {
    return this.isOpen ? 'close' : 'menu'
  }

  toggleSidebarOpened(): void {
    this.sidebarService.getSidebar().toggleOpen()
  }

  toggleSidebarFolded(): void {
    this.isOpen = !this.isOpen
    this.sidebarService.getSidebar().toggleFold()
  }

  private isHelpExist = (): boolean => {
    const isHelpIcon = ({ icon, title }: Navigation): boolean => icon === 'help_center' || title === 'NAV.SUPPORT'
    return !this.navigation?.some(isHelpIcon)
  }

  private addSupportToMenu(): void {
    if (!this.isHelpExist()) return

    const supportItem: Navigation = {
      title: 'NAV.SUPPORT',
      icon: 'help_center',
      permission: PermissionsSource.SUPPORT,
      url: `/${this.user.teamType}/support-inbox`,
    }

    this.originalMenu.push(supportItem)
    this.navigation.push(supportItem)
  }
}
